/**
 * DecisionFlow — AIModeSection
 * ui/src/components/platform-admin/AIModeSection.jsx
 * S1034 — Agent Operating Mode — Provision Form Section
 *
 * Slotted into TenantProvision.jsx between Axis 3 connectors and Billing Schedule.
 * spec_agent_operating_mode_s1033.md §8.1
 * DECISION-S1033-13: mode selection at provision time.
 *
 * Defaults (per spec):
 *   starting_mode = 'T2'   (Full Copilot)
 *   mode_ceiling  = 'T2'   (no restriction)
 *   ca_kill_switch_enabled = true  (CA gets kill switch by default)
 *   ca_permitted_modes = ['T1','T2'] (CA gets full mode range)
 *
 * UI constraint: starting_mode cannot exceed mode_ceiling (enforced client-side).
 * Values are passed to TenantProvision's handleSubmit via the aiMode prop callback.
 *
 * Usage in TenantProvision.jsx:
 *   import AIModeSection from './AIModeSection.jsx'
 *
 *   const [aiMode, setAiMode] = useState({
 *     starting_mode:          'T2',
 *     mode_ceiling:           'T2',
 *     ca_kill_switch_enabled: true,
 *     ca_permitted_modes:     ['T1','T2'],
 *   })
 *
 *   // In form body, after Axis 3 connectors, before Billing Schedule:
 *   <AIModeSection value={aiMode} onChange={setAiMode} disabled={submitting} />
 *
 *   // In body construction:
 *   starting_mode:          aiMode.starting_mode,
 *   mode_ceiling:           aiMode.mode_ceiling,
 *   ca_kill_switch_enabled: aiMode.ca_kill_switch_enabled,
 *   ca_permitted_modes:     aiMode.ca_permitted_modes,
 *
 * Design rules (match TenantProvision.jsx):
 *   rgb(var(--color-*)) only
 *   text-tok-* sizes
 *   SectionLabel, FieldRow patterns match existing form
 */

const MODE_LABELS = {
  T0: "Agent Off",
  T1: "Advise Only",
  T2: "Full Copilot",
};

const MODE_DESCRIPTIONS = {
  T0: "No AI agent activity. No sessions, no tool calls.",
  T1: "Agent reads and explains. No proposals or write actions.",
  T2: "Full Copilot — all Gates 0–8, proposals, and remediations.",
};

const MODE_RANK = { T0: 0, T1: 1, T2: 2 };

/**
 * AIModeSection
 *
 * @param {object}   value    - Current AI mode state
 * @param {function} onChange - Callback: (newValue) => void
 * @param {boolean}  disabled - Disabled state (form submitting)
 */
export default function AIModeSection({ value, onChange, disabled }) {
  const {
    starting_mode          = "T2",
    mode_ceiling           = "T2",
    ca_kill_switch_enabled = true,
    ca_permitted_modes     = ["T1", "T2"],
  } = value ?? {};

  function update(patch) {
    const next = { starting_mode, mode_ceiling, ca_kill_switch_enabled, ca_permitted_modes, ...patch };

    // Enforce: starting_mode cannot exceed mode_ceiling
    if (MODE_RANK[next.starting_mode] > MODE_RANK[next.mode_ceiling]) {
      next.starting_mode = next.mode_ceiling;
    }

    // Enforce: ca_permitted_modes cannot contain modes above ceiling
    next.ca_permitted_modes = (next.ca_permitted_modes ?? []).filter(
      (m) => MODE_RANK[m] <= MODE_RANK[next.mode_ceiling]
    );

    // When switching to T2, restore full defaults for ceiling and CA delegation
    if (next.starting_mode === "T2" && starting_mode !== "T2") {
      next.mode_ceiling           = "T2";
      next.ca_permitted_modes     = ["T1", "T2"];
      next.ca_kill_switch_enabled = true;
    }

    // When switching to T1: ceiling=T1, CA permitted=T1 only, kill switch on
    // T2 stripped by ceiling enforcement above; this sets the sensible T1 defaults
    if (next.starting_mode === "T1" && starting_mode !== "T1") {
      next.mode_ceiling           = "T1";
      next.ca_permitted_modes     = ["T1"];
      next.ca_kill_switch_enabled = true;
    }

    // When agent is off (T0), CA delegation is meaningless — clear it so provision saves clean state
    if (next.starting_mode === "T0") {
      next.ca_permitted_modes = [];
      next.ca_kill_switch_enabled = false;
    }

    onChange(next);
  }

  function toggleCaPermitted(mode) {
    const next = ca_permitted_modes.includes(mode)
      ? ca_permitted_modes.filter((m) => m !== mode)
      : [...ca_permitted_modes, mode];
    update({ ca_permitted_modes: next });
  }

  // Available modes for starting_mode selector: T1 and T2 only (T0 = agent off at provision time is valid but unusual)
  const allModes = ["T0", "T1", "T2"];

  // Available modes for ceiling: T1, T2 (T0 ceiling = permanently off — allowed but flagged)
  const ceilingModes = ["T1", "T2"];

  // When starting_mode = T0, agent is off — CA delegation is irrelevant.
  // Delegation section is disabled and ca_permitted_modes is treated as empty.
  const agentOff = starting_mode === "T0";

  // Available modes for CA permitted: only T1 and T2 (T0 is kill switch, not in ca_permitted_modes)
  const delegateModes = ["T1", "T2"].filter((m) => MODE_RANK[m] <= MODE_RANK[mode_ceiling]);

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div>
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          AI Operating Mode
        </p>
        <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Sets the agent's starting mode and governance permissions for this tenant.
          Can be changed after provisioning via Tenant Admin → AI Governance.
        </p>
      </div>

      {/* Starting mode */}
      <div className="space-y-2">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Starting mode
        </p>
        <div className="space-y-1.5">
          {allModes.map((mode) => {
            const exceedsCeiling = MODE_RANK[mode] > MODE_RANK[mode_ceiling];
            return (
              <label
                key={mode}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition ${
                  starting_mode === mode
                    ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5"
                    : "border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]"
                } ${exceedsCeiling || disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <input
                  type="radio"
                  name="ai_starting_mode"
                  value={mode}
                  checked={starting_mode === mode}
                  onChange={() => !exceedsCeiling && update({ starting_mode: mode })}
                  disabled={disabled || exceedsCeiling}
                  className="mt-0.5 shrink-0"
                />
                <div>
                  <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                    {MODE_LABELS[mode]}
                  </p>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                    {MODE_DESCRIPTIONS[mode]}
                    {exceedsCeiling && " (exceeds ceiling — unavailable)"}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Mode ceiling */}
      <div className="space-y-2">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Mode ceiling
        </p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          Maximum mode Super Admin can set for this tenant. Starting mode cannot exceed this.
        </p>
        <div className="flex gap-2">
          {ceilingModes.map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => update({ mode_ceiling: mode })}
              disabled={disabled}
              className={`rounded-xl border px-4 py-2 text-tok-sm font-semibold transition ${
                mode_ceiling === mode
                  ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]"
                  : "border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
              } disabled:opacity-50`}
            >
              {MODE_LABELS[mode]}
            </button>
          ))}
        </div>
      </div>

      {/* CA Delegation */}
      <div className={`rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 space-y-4 ${agentOff ? "opacity-40 pointer-events-none" : ""}`}>
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          Comp Admin Delegation
          {agentOff && <span className="ml-2 normal-case font-normal">(unavailable — agent is off)</span>}
        </p>

        {/* CA mode switching */}
        <div className="space-y-2">
          <p className="text-tok-xs font-medium text-[rgb(var(--color-text))]">
            Mode switching — permitted modes for Comp Admin
          </p>
          {delegateModes.length > 0 ? (
            <div className="flex gap-4">
              {delegateModes.map((mode) => (
                <label key={mode} className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={agentOff ? false : ca_permitted_modes.includes(mode)}
                    onChange={() => toggleCaPermitted(mode)}
                    disabled={disabled || agentOff}
                    className="h-4 w-4 rounded border-[rgb(var(--color-border))]"
                  />
                  <span className="text-tok-sm text-[rgb(var(--color-text))]">{MODE_LABELS[mode]}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              No modes available within current ceiling for CA delegation.
            </p>
          )}
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Empty = Comp Admin has no mode switching access.
          </p>
        </div>

        {/* CA kill switch */}
        <div className="space-y-1">
          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              checked={agentOff ? false : ca_kill_switch_enabled}
              onChange={(e) => update({ ca_kill_switch_enabled: e.target.checked })}
              disabled={disabled || agentOff}
              className="h-4 w-4 rounded border-[rgb(var(--color-border))]"
            />
            <div>
              <p className="text-tok-sm text-[rgb(var(--color-text))]">
                Allow Comp Admin to activate / deactivate the kill switch
              </p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                Independent of mode switching. When enabled, CA sees a Halt Agent button in Trust Center.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Summary */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))] space-y-0.5">
        <p>
          <span className="font-semibold text-[rgb(var(--color-text))]">Starting mode:</span>{" "}
          {MODE_LABELS[starting_mode]}
        </p>
        <p>
          <span className="font-semibold text-[rgb(var(--color-text))]">Ceiling:</span>{" "}
          {MODE_LABELS[mode_ceiling]}
        </p>
        <p>
          <span className="font-semibold text-[rgb(var(--color-text))]">CA mode switching:</span>{" "}
          {ca_permitted_modes.length > 0 ? ca_permitted_modes.map((m) => MODE_LABELS[m] ?? m).join(", ") : "None"}
        </p>
        <p>
          <span className="font-semibold text-[rgb(var(--color-text))]">CA kill switch:</span>{" "}
          {ca_kill_switch_enabled ? "Enabled" : "Disabled"}
        </p>
      </div>
    </div>
  );
}
